import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { HasSlotController } from '../controllers/HasSlotController'
import { LitHauntedElement } from '../mixins/lit-haunted-element'
import { useEffect } from 'haunted'

function isNotNullOrWhitespace(x: string | undefined | null) {
  return x && x.trim() !== '';
}

export type ChipVariant = 'filled' | 'outlined'

/**
 * A "chip" element
 */
@customElement('ep-chip')
export class EPChip extends LitHauntedElement {
  private hasSlotController = new HasSlotController(this, 'leading-icon', 'trailing-icon');

  @property({ type: Boolean, reflect: true })
  clickable: boolean = false;

  @property({ reflect: true })
  variant: ChipVariant = 'filled'

  @property({ reflect: true })
  href?: string;

  @property({ reflect: true })
  target?: string;

  get href2(): string | undefined {
    return isNotNullOrWhitespace(this.href) ? this.href : undefined;
  }

  get hasIconSlotted(): boolean {
    return this.hasSlotController.testAny('leading-icon', 'trailing-icon');
  }

  render() {
    useEffect(() => {
      if (this.href2 !== undefined) {
        this.clickable = true;
      }
    }, [this.href]);
    
    return html`
      <a href=${ifDefined(this.href2)} target=${ifDefined(this.target)} class="chipRoot ${this.variant} ${this.clickable ? 'clickable' : ''}" ?data-iconslotted=${this.hasIconSlotted}>
        <slot name="leading-icon"></slot>
        <span class="chipLabel">
          <slot></slot>
        </span>
        <slot name="trailing-icon"></slot>
      </a>
    `
  }

  static styles = css`
    :host {
      display: inline-block;
      --filled-background: rgba(0, 0, 0, 0.08);
      --filled-background-hover: rgba(0, 0, 0, 0.12);
      --outlined-background: transparent;
      --outlined-background-hover: rgba(0, 0, 0, 0.04);
      --outlined-border: 1px solid #bdbdbd;
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      --inner-chip-height: var(--chip-height, 32px);
      max-width: inherit;
    }

    .chipRoot {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      /* height: var(--chip-height); */
      height: auto;
      width: inherit;
      max-width: inherit;
      color: rgba(0, 0, 0, 0.87);
      border-radius: calc(var(--inner-chip-height) / 2);
      white-space: nowrap;
      cursor: default;
      outline: 0;
      text-decoration: none;
      border: 0;
      padding: 0 5px;
      vertical-align: middle;
      box-sizing: border-box;
      transition: background-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 125ms ease 0ms;
    }

    /* .chipRoot[mini] {
      height: auto;
    } */

    .chipRoot.clickable {
      cursor: pointer;
      user-select: none;
    }
    .chipRoot.clickable:hover {
      transform: scale(1.025);
    }
    .chipRoot.clickable:active {
      transform: scale(0.925);
    }

    .chipRoot.filled {
      background-color: var(--filled-background);
    }
    .chipRoot.filled.clickable:hover {
      background-color: var(--filled-background-hover);
    }
    
    .chipRoot.outlined {
      background-color: var(--outlined-background);
      border: var(--outlined-border);
    }
    .chipRoot.outlined.clickable:hover {
      background-color: var(--outlined-background-hover);
    }

    .chipLabel {
      font-family: var(--font-prose);
      font-size: 0.8125rem;
      color: rgba(0, 0, 0, 0.87);
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 calc(var(--inner-chip-height) * 0.375);
      white-space: nowrap;
      line-height: var(--inner-chip-height);
    }

    ::slotted([slot="leading-icon"]) {
      margin-left: 5px;
      margin-right: -6px;
      user-select: none;
      /* width: 1em; */
      width: calc(var(--inner-chip-height) * 0.75);
      /* height: 1em; */
      height: calc(var(--inner-chip-height) * 0.75);
      display: inline-block;
      fill: currentColor;
      flex-shrink: 0;
      transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      font-size: 1.0em;
    }

    ::slotted([slot="trailing-icon"]) {
      margin-right: 5px;
      margin-left: -6px;
      user-select: none;
      /* width: 1em; */
      width: calc(var(--inner-chip-height) * 0.75);
      /* height: 1em; */
      height: calc(var(--inner-chip-height) * 0.75);
      display: inline-block;
      fill: currentColor;
      flex-shrink: 0;
      transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      font-size: 1.0em;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-chip': EPChip
  }
}
